import { ErrorReporter } from '@wix/editor-error-reporter';
import type {
  CompData,
  CompLayout,
  CompRef,
  DocumentServicesObject,
  SerializedCompStructure,
  SerializedPageStructure,
  StyleRef,
  StyleRefOrStyleRefs,
} from '@wix/document-services-types';
import { getWebAnimation } from '@wix/motion';
import constants from '#packages/constants';
import { type PreviewerEffectsMapping } from './types';

const { COMP_TYPES } = constants;

export const getPreviewerEffectsMapping = (
  miniDs: DocumentServicesObject,
  rootPointers: CompRef[],
): PreviewerEffectsMapping => {
  const children = rootPointers.flatMap((rootPointer) =>
    miniDs.components.getChildren(rootPointer, true),
  );
  return children.reduce((acc, childRef) => {
    const effectRefs = miniDs.components.effects.list(childRef);
    if (effectRefs.length === 0) return acc;
    return {
      ...acc,
      [childRef.id]: effectRefs
        .map((effectRef) => ({
          effectId: effectRef.id,
          animationOptions: miniDs.components.effects.get(childRef, effectRef)
            ?.value,
        }))
        .filter((effectData) => effectData.animationOptions?.namedEffect),
    };
  }, {});
};

export const runPreviewAnimation = (
  shadowRoot: ShadowRoot | Document,
  effectsMapping: PreviewerEffectsMapping,
) => {
  const observer = new IntersectionObserver(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio > 0) {
          const element = entry.target as HTMLElement;
          const elementEffects = effectsMapping[element.id];
          if (!elementEffects?.length) return;
          const { animationOptions, effectId } = elementEffects[0];
          const { delay, duration, allowReplay, namedEffect } =
            animationOptions;
          const numericDuration =
            typeof duration === 'number' ? duration : 1000;
          try {
            const motion = getWebAnimation(
              element.id,
              {
                id: effectId,
                type: 'TimeAnimationOptions',
                namedEffect: {
                  id: effectId,
                  // @ts-expect-error
                  type: namedEffect.type || '',
                  allowReplay: 'perPageView',
                },
                duration: numericDuration,
                delay,
                fill: 'backwards',
                iterations: 1,
                easing: '',
              },
              undefined,
              undefined,
              shadowRoot,
            );
            motion.play();
            element.style.opacity = '1';
          } catch (e) {
            console.error(e);
            ErrorReporter.captureException(e, {
              tags: { previewerRunMotion: true },
              extra: {
                delay,
                duration,
                namedEffect,
                allowReplay,
              },
            });
            element.style.opacity = '1';
          }
          observer.unobserve(element);
        }
      });
    },
    { rootMargin: '50% 0px', threshold: 0.15 },
  );
  Object.keys(effectsMapping).forEach((compId) => {
    const el = shadowRoot.getElementById(compId);
    if (el) {
      el.style.opacity = '0';
      observer.observe(el);
    }
  });
};

export function getDefaultBaseWidth(): number {
  return Math.max(
    window.document.body.getBoundingClientRect().width - 100,
    980,
  );
}

export const replaceStyle = (
  ds: DocumentServicesObject,
  ref: CompRef,
  style?: string | StyleRefOrStyleRefs,
) => {
  if (!style) return;
  if (typeof style === 'string') {
    if (ds.theme.styles.get(style)) {
      ds.components.style.connectToTheme(ref, style);
    }
  } else {
    ds.components.style.update(ref, style as StyleRef);
  }
};

export const replaceDesign = (
  ds: DocumentServicesObject,
  ref: CompRef,
  design?: SerializedCompStructure['design'],
) => {
  if (!design) return;
  ds.components.design.update(ref, design);
};

const replaceLayout = (
  ds: DocumentServicesObject,
  ref: CompRef,
  layout?: Partial<CompLayout>,
) => {
  if (!layout) return;
  ds.components.layout.update(ref, {
    height: layout.height,
    fixedPosition: Boolean(layout.fixedPosition),
  });
};

const adjustBehaviors = (
  ds: DocumentServicesObject,
  ref: CompRef,
  compStructure: SerializedCompStructure,
) => {
  if (!compStructure.behaviors) return;
  const parsedBehaviorsItems = JSON.parse(compStructure.behaviors.items);
  ds.components.behaviors.update(
    ref,
    parsedBehaviorsItems[0],
    //TODO: remove ts-ignore when types are fixed (https://wix.slack.com/archives/C9N00LLE6/p1701783454664289)
    //@ts-expect-error
    parsedBehaviorsItems[0].action,
    parsedBehaviorsItems[0].viewMode,
  );
};

const fixDataApplicationId = (
  ds: DocumentServicesObject,
  compData: CompData,
): void => {
  if (!compData?.applicationId || !compData?.appDefinitionId) {
    return;
  }
  const applicationId = ds.platform.getAppDataByAppDefId(
    compData.appDefinitionId,
  )?.applicationId;
  if (!applicationId) {
    return;
  }
  compData.applicationId = `${applicationId}`;
};

export const fixPresetStructureApplicationIds = (
  ds: DocumentServicesObject,
  presetStructure: SerializedCompStructure | SerializedPageStructure,
): void => {
  fixDataApplicationId(ds, presetStructure.data);
  presetStructure.components?.forEach((childStructure) => {
    fixPresetStructureApplicationIds(ds, childStructure);
  });
};

const replaceChildComps = (
  ds: DocumentServicesObject,
  ref: CompRef,
  replacement: SerializedCompStructure,
) => {
  const childComps = ds.components.getChildren(ref);
  childComps.forEach((component) => ds.components.remove(component));

  replacement.components?.forEach((component) => {
    ds.components.add(ref, component);
  });
};

export const replaceHeaderOrFooter = (
  ds: DocumentServicesObject,
  ref: CompRef,
  compStructure: SerializedCompStructure,
) => {
  adjustBehaviors(ds, ref, compStructure);
  replaceLayout(ds, ref, compStructure.layout);
  replaceStyle(ds, ref, compStructure.style);
  replaceDesign(ds, ref, compStructure.design);
  fixPresetStructureApplicationIds(ds, compStructure);
  replaceChildComps(ds, ref, compStructure);
};

const replaceHeader = (
  ds: DocumentServicesObject,
  headerStructure: SerializedCompStructure,
) => {
  if (headerStructure.componentType !== COMP_TYPES.HEADER) {
    throw new Error(
      `replaceHeader called with ${headerStructure.componentType} type`,
    );
  }
  replaceHeaderOrFooter(ds, ds.siteSegments.getHeader(), headerStructure);
};

const replaceFooter = (
  ds: DocumentServicesObject,
  footerStructure?: SerializedCompStructure,
) => {
  if (!footerStructure?.componentType) {
    return;
  }
  if (footerStructure.componentType !== COMP_TYPES.FOOTER) {
    throw new Error(
      `replaceFooter called with ${footerStructure.componentType} type`,
    );
  }
  replaceHeaderOrFooter(ds, ds.siteSegments.getFooter(), footerStructure);
};

export const adjustHeaderAndFooterIfNeeded = async (
  miniDs: DocumentServicesObject,
  pageStructure: SerializedPageStructure,
  headerStructure?: SerializedCompStructure,
  footerStructure?: SerializedCompStructure,
): Promise<void> => {
  if (pageStructure.data?.isLandingPage) {
    return;
  }
  if (headerStructure) {
    replaceHeader(miniDs, headerStructure);
  }
  if (footerStructure) {
    replaceFooter(miniDs, footerStructure);
  }
  await miniDs.waitForChangesAppliedAsync();
};

export const setUsesNewAnimations = (ds: DocumentServicesObject) => {
  const masterPageRef = ds.components.get.byId(
    ds.pages.getMasterPageId(),
  ) as CompRef;
  ds.components.data.update(masterPageRef, { usesNewAnimations: true });
};
